/**
 * Brand (product) and variant selection shared by the consumables tagging
 * and the dispensing (onboarding).
 *
 * Priority of the pre-selected brand/variant:
 * 1. the default set in the tagging of the service (e.g. CBC -> Syringe -> Terumo 3cc)
 * 2. the default brand / default variant (Branded Products)
 * 3. the only brand / only variant
 */

// branch brands of a generic
export const brandsOf = (products = [], pgid) =>
  products.filter((product) => product.pgid === Number(pgid));

// default brand of the generic, or its only brand
export const autoBrand = (products, pgid) => {
  const brands = brandsOf(products, pgid);
  return (
    brands.find(({ isDefault }) => isDefault) ||
    (brands.length === 1 ? brands[0] : null)
  );
};

// default variant of the brand, or its only variant
export const autoVariant = (product) => {
  const variants = product?.variants || [];
  return (
    variants.find(({ isDefault }) => isDefault) ||
    (variants.length === 1 ? variants[0] : null)
  );
};

const findVariant = (product, variantId) =>
  (product?.variants || []).find(({ _id }) => _id === variantId) || null;

/**
 * Brand + variant to pre-select for a generic.
 * `tag` is the tagging item of the service ({ product, variant }), optional.
 * A tagged brand that no longer exists falls back to the defaults.
 */
export const autoSelect = (products, pgid, tag = {}) => {
  const tagged = tag.product
    ? brandsOf(products, pgid).find(
        ({ _id }) => _id === (tag.product?._id || tag.product),
      )
    : null;
  if (tagged)
    return {
      product: tagged,
      variant:
        findVariant(tagged, tag.variant?._id || tag.variant) ||
        autoVariant(tagged),
    };

  const product = autoBrand(products, pgid);
  return { product, variant: autoVariant(product) };
};

// brand chosen, and its variant when the brand has variants
export const isComplete = ({ product, variant }) =>
  !!product && (!product.variants?.length || !!variant);

/* ---------------------------------------------------------------------------
 * Stock (dispensing only). `stock` is { "productId:variantId": onHand } in base
 * units (pcs, or tests for reagents), from the received purchase orders.
 * The tagging only checks that a brand is registered, never the stock.
 * ------------------------------------------------------------------------- */

const idOf = (value) => value?._id || value || "";

export const stockKey = (product, variant) =>
  `${idOf(product)}:${idOf(variant)}`;

export const onHandOf = (stock = {}, product, variant) =>
  stock[stockKey(product, variant)] || 0;

// total on hand of a brand across its variants
export const brandOnHand = (stock = {}, product) =>
  product?.variants?.length
    ? product.variants.reduce(
        (sum, variant) => sum + onHandOf(stock, product, variant),
        0,
      )
    : onHandOf(stock, product);

/**
 * The pre-selected brand/variant is only kept when it has enough stock;
 * otherwise nothing is selected and the user must pick an available one.
 * `unavailable` keeps the out-of-stock default for the message.
 */
export const withStock = (selection, stock, qty = 1) =>
  selection.product &&
  onHandOf(stock, selection.product, selection.variant) < qty
    ? { product: null, variant: null, unavailable: selection }
    : { ...selection, unavailable: null };
